import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/authService';
import toast from 'react-hot-toast';
import {
  Settings, CheckCircle, AlertCircle, ArrowLeft, Save,
  Wrench, Calendar, Gauge, Clock, FileText, Info
} from 'lucide-react';

const VehicleMaintenanceSetup = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [baselineStatus, setBaselineStatus] = useState(null);

  const [currentOdometer, setCurrentOdometer] = useState('');
  const [currentEngineHours, setCurrentEngineHours] = useState('');
  const [componentsData, setComponentsData] = useState([]);

  useEffect(() => {
    loadData();
  }, [vehicleId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Buscar informações do veículo
      const vehicleResponse = await api.get(`/api/vehicles/${vehicleId}`);
      setVehicle(vehicleResponse.data);

      // Buscar status do baseline
      const baselineResponse = await api.get(
        `/api/predictive-maintenance/vehicles/${vehicleId}/baseline-status`
      );

      setBaselineStatus(baselineResponse.data.data);

      // Inicializar dados dos componentes
      const initialData = baselineResponse.data.data.components.map(comp => ({
        component_category: comp.component_category,
        component_name: comp.component_name,
        has_baseline: comp.has_baseline,
        maintenance_interval_km: comp.maintenance_interval_km,
        maintenance_interval_hours: comp.maintenance_interval_hours,
        maintenance_interval_days: comp.maintenance_interval_days,
        installed_km: comp.last_maintenance?.installed_km || '',
        installed_hours: comp.last_maintenance?.installed_hours || '',
        installed_date: comp.last_maintenance?.installed_at?.split('T')[0] || '',
        notes: comp.last_maintenance?.notes || ''
      }));

      setComponentsData(initialData);

      // Preencher leitura atual se existir
      if (baselineResponse.data.data.last_reading) {
        setCurrentOdometer(baselineResponse.data.data.last_reading.odometer_reading || '');
        setCurrentEngineHours(baselineResponse.data.data.last_reading.engine_hours_reading || '');
      } else if (vehicleResponse.data.odometer) {
        setCurrentOdometer(vehicleResponse.data.odometer || '');
        setCurrentEngineHours(vehicleResponse.data.engine_hours || '');
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar informações do veículo');
    } finally {
      setLoading(false);
    }
  };

  const handleComponentChange = (index, field, value) => {
    const updated = [...componentsData];
    updated[index][field] = value;
    setComponentsData(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentOdometer) {
      toast.error('Informe o hodômetro atual');
      return;
    }

    // Filtrar apenas componentes que foram preenchidos
    const componentsToSubmit = componentsData.filter(comp =>
      comp.installed_km || comp.installed_hours || comp.installed_date
    );

    if (componentsToSubmit.length === 0) {
      toast.error('Informe pelo menos um componente');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        current_odometer: parseInt(currentOdometer),
        current_engine_hours: currentEngineHours ? parseInt(currentEngineHours) : null,
        components: componentsToSubmit.map(comp => ({
          component_category: comp.component_category,
          component_name: comp.component_name,
          installed_km: comp.installed_km ? parseInt(comp.installed_km) : null,
          installed_hours: comp.installed_hours ? parseInt(comp.installed_hours) : null,
          installed_date: comp.installed_date || null,
          notes: comp.notes || null
        }))
      };

      const response = await api.post(
        `/api/predictive-maintenance/vehicles/${vehicleId}/setup-baseline`,
        payload
      );

      toast.success(response.data.message);

      // Redirecionar para a página de manutenção preditiva
      setTimeout(() => {
        navigate('/predictive-maintenance');
      }, 1500);

    } catch (error) {
      console.error('Erro ao configurar baseline:', error);
      toast.error(error.response?.data?.message || 'Erro ao configurar baseline');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      engine: '🔧',
      transmission: '⚙️',
      brakes: '🛑',
      suspension: '🔩',
      electrical: '⚡',
      hydraulic: '💧',
      tires: '🛞',
      other: '📦'
    };
    return icons[category] || '🔧';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      engine: 'Motor',
      transmission: 'Transmissão',
      brakes: 'Freios',
      suspension: 'Suspensão',
      electrical: 'Elétrico',
      hydraulic: 'Hidráulico',
      tires: 'Pneus',
      other: 'Outros'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!vehicle || !baselineStatus) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">Erro ao carregar dados do veículo.</p>
        </div>
      </div>
    );
  }

  const progress = baselineStatus.total_components > 0
    ? (baselineStatus.components_with_baseline / baselineStatus.total_components) * 100
    : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Voltar
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Settings className="w-8 h-8 text-blue-500" />
              Setup Inicial de Manutenções
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Configure quando cada componente foi trocado pela última vez
            </p>
          </div>
        </div>
      </div>

      {/* Vehicle Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Wrench className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {vehicle.brand} {vehicle.model}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Placa: {vehicle.license_plate} • Tipo: {getCategoryLabel(vehicle.type)}
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600 dark:text-gray-400">Progresso</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {Math.round(progress)}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {baselineStatus.components_with_baseline} de {baselineStatus.total_components} componentes
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-900 dark:text-blue-100">
          <p className="font-semibold mb-1">Como funciona o monitoramento automático:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Configure o hodômetro/horas atuais do veículo</li>
            <li>Informe quando cada componente foi trocado pela última vez</li>
            <li>O sistema calculará automaticamente quando a próxima manutenção é necessária</li>
            <li>Você receberá alertas quando atingir 80% e 95% do intervalo</li>
          </ol>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        {/* Current Readings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Gauge className="w-5 h-5 text-blue-500" />
            Leituras Atuais do Veículo
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Hodômetro Atual (km) *
              </label>
              <input
                type="number"
                value={currentOdometer}
                onChange={(e) => setCurrentOdometer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 50000"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Horas do Motor (opcional)
              </label>
              <input
                type="number"
                value={currentEngineHours}
                onChange={(e) => setCurrentEngineHours(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 1000"
              />
            </div>
          </div>
        </div>

        {/* Components */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-blue-500" />
            Histórico de Componentes
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
              (preencha os que souber)
            </span>
          </h3>

          <div className="space-y-4">
            {componentsData.map((comp, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${
                  comp.has_baseline
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl">{getCategoryIcon(comp.component_category)}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {comp.component_name}
                      </h4>
                      {comp.has_baseline && (
                        <span className="flex items-center gap-1 text-xs bg-green-100 dark:bg-green-900/30
                                       text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          Configurado
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {comp.maintenance_interval_km && `A cada ${comp.maintenance_interval_km.toLocaleString()} km`}
                      {comp.maintenance_interval_km && comp.maintenance_interval_hours && ' • '}
                      {comp.maintenance_interval_hours && `${comp.maintenance_interval_hours}h`}
                      {(comp.maintenance_interval_km || comp.maintenance_interval_hours) && comp.maintenance_interval_days && ' • '}
                      {comp.maintenance_interval_days && `${comp.maintenance_interval_days} dias`}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {comp.maintenance_interval_km && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        KM da última troca
                      </label>
                      <input
                        type="number"
                        value={comp.installed_km}
                        onChange={(e) => handleComponentChange(index, 'installed_km', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded
                                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                   focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Ex: 40000"
                      />
                    </div>
                  )}

                  {comp.maintenance_interval_hours && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Horas da última troca
                      </label>
                      <input
                        type="number"
                        value={comp.installed_hours}
                        onChange={(e) => handleComponentChange(index, 'installed_hours', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded
                                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                   focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Ex: 800"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Data da última troca
                    </label>
                    <input
                      type="date"
                      value={comp.installed_date}
                      onChange={(e) => handleComponentChange(index, 'installed_date', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Observações (opcional)
                    </label>
                    <input
                      type="text"
                      value={comp.notes}
                      onChange={(e) => handleComponentChange(index, 'notes', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: Trocado na oficina X"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100
                     dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={saving || !currentOdometer}
            className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-lg
                     hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Salvar Baseline
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VehicleMaintenanceSetup;
